import {
  ActionButton,
  DirectionalHint,
  IconButton,
  makeStyles,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DataAccessPolicyValues } from '../types';
import { FormFields } from './FormFields';
import { DataAccessPolicyCommonData_companyBusinessUnits } from '../__generated__/DataAccessPolicyCommonData';
import { BUSINESS_UNIT_ACCESS_INITIAL_VALUES } from '../constant';
import React from 'react';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.black,
    alignSelf: 'end',
    margin: '0px 4px',
  },
}));

const BUSINESS_UNIT_ACCESS_TEXT =
  'Enter the primary business unit used for the access policy. If access is not unlimited, select the additional business units this policy will allow the user to access.';

interface BusinessUnitAccessFormProps {
  companyBusinessUnits:
    | DataAccessPolicyCommonData_companyBusinessUnits
    | null
    | undefined;
  _isUpdatable: boolean;
}

export const BusinessUnitAccessForm: React.FC<BusinessUnitAccessFormProps> = ({
  companyBusinessUnits,
  _isUpdatable,
}) => {
  const styles = useStyles();
  const { control, setValue } = useFormContext<DataAccessPolicyValues>();
  const { fields, remove, append } = useFieldArray({
    name: 'dataAccessBusinessUnits',
    control,
  });
  const onAdd = () => {
    append(BUSINESS_UNIT_ACCESS_INITIAL_VALUES);
  };
  const _onPrimaryChange = (index: number, checked: boolean) => {
    if (fields.length === 1 || !checked) {
      setValue(`dataAccessBusinessUnits.${index}.isPrimary`, checked, {
        shouldDirty: true,
      });
    } else {
      fields?.forEach((_ele, i) => {
        setValue(`dataAccessBusinessUnits.${i}.isPrimary`, i === index, {
          shouldDirty: true,
        });
      });
    }
  };
  return (
    <Stack tokens={{ childrenGap: 10, padding: '20px 20px 100px' }}>
      <TooltipHost
        content={BUSINESS_UNIT_ACCESS_TEXT}
        directionalHint={DirectionalHint.topAutoEdge}
      >
        <Text variant="xLarge">Business Unit Access</Text>
      </TooltipHost>
      {fields.map((row, index) => {
        const baseField = `dataAccessBusinessUnits.${index}`;
        return (
          <Stack
            horizontal
            verticalAlign="end"
            key={row.id}
            style={{ marginTop: 20 }}
          >
            <FormFields
              baseField={baseField}
              businessUnitAccessDisabled={!!row.id && !!row._rowTimestamp}
              companyBusinessUnits={companyBusinessUnits}
              isUpdatable={!!row._isUpdatable}
              onPrimaryChange={(checked) => {
                _onPrimaryChange(index, checked);
              }}
            />
            {
              <TooltipHost content="Remove" id="tooltipId">
                <IconButton
                  iconProps={{ iconName: 'Cancel' }}
                  className={styles.icon}
                  onClick={() => remove(index)}
                  disabled={!row._isDeletable}
                />
              </TooltipHost>
            }
          </Stack>
        );
      })}
      <Stack horizontalAlign="start">
        <ActionButton
          iconProps={{ iconName: 'add' }}
          text="Add"
          onClick={onAdd}
          disabled={!_isUpdatable}
        />
      </Stack>
    </Stack>
  );
};
